import { Box, Slider, Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import { SoftWarning } from "web_myplann/Inspiration/Components/SoftWarning";
import type { SliderBounds } from "web_myplann/Inspiration/Types/SliderBounds";
import { formatInspirationMoney } from "web_myplann/Inspiration/Utils/inspirationMoney";
import { isValueUnusual } from "web_myplann/Inspiration/Utils/isValueUnusual";

const SLIDER_HEIGHT = 30;

export type BoxedSliderFieldProps = SliderBounds & {
	label: string;
	value: number;
	onChange: (value: number) => void;
	hint?: string;
	formatValue?: (value: number) => string;
	minLabel?: string;
	maxLabel?: string;
};

export const BoxedSliderField: FC<BoxedSliderFieldProps> = ({
	label,
	value,
	onChange,
	min,
	max,
	step,
	hint,
	softMin,
	softMax,
	formatValue = formatInspirationMoney,
	minLabel,
	maxLabel,
}) => {
	const theme = useTheme();
	return (
		<Box
			bgcolor={theme.palette.surface.muted}
			borderRadius={theme.radii.lg}
			p={theme.spacingTokens.md}
		>
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Typography variant="bodyMedium">{label}</Typography>
				<Typography variant="subtitle3" color={theme.palette.blue.main} tabular>
					{formatValue(value)}
				</Typography>
			</Stack>
			<Slider
				variant="flat"
				value={value}
				onChange={(_, v) => onChange(Array.isArray(v) ? v[0] : v)}
				min={min}
				max={max}
				step={step}
				sx={{ height: SLIDER_HEIGHT, p: 0, mt: theme.spacingTokens.xs }}
			/>
			<Stack direction="row" justifyContent="space-between">
				<Typography variant="hint" tabular>
					{minLabel ?? formatValue(min)}
				</Typography>
				<Typography variant="hint" tabular>
					{maxLabel ?? formatValue(max)}
				</Typography>
			</Stack>
			{isValueUnusual(value, { softMin, softMax }) && <SoftWarning />}

			{hint && (
				<Typography
					variant="hint"
					color={theme.palette.grey[500]}
					mt={theme.spacingTokens.sm}
					display="block"
				>
					{hint}
				</Typography>
			)}
		</Box>
	);
};
